import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@App/app/auth/auth.service';
import { selectIdToken } from '@App/app/auth/store/auth/selectors/auth.selectors';
import { RefreshedTokens } from '@App/app/entities/auth/tokens.model';
import { Store } from '@ngrx/store';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { environment } from '@App/environments/environment';
import { addBearerToken, retryAfterTokenRefresh } from './shared/refresh-token-retry.util';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<RefreshedTokens | null>(null);

  constructor(private authService: AuthService, private store: Store) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const api = this.getApiPath();

    let idToken = '';
    this.store
      .select(selectIdToken)
      .pipe(take(1))
      .subscribe((token) => {
        idToken = token;
      });

    if (idToken && (request.url.includes(api) || request.url.includes(environment.loginUrl))) {
      request = addBearerToken(request, idToken);
    }

    return next.handle(request).pipe(catchError((error) => this.handleError(error, request, next)));
  }

  private getApiPath(): string {
    let api = '';
    this.authService.endpointPath.pipe(take(1)).subscribe((path) => {
      api = path ?? '';
    });
    return api;
  }

  private handleError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler) {
    switch (error?.status) {
      case 401:
        return this.authService.isAuth() ? this.handle401Error(request, next) : EMPTY;
      case 403:
        return this.handle403Error(error, request);
      default:
        return throwError(error);
    }
  }

  private handle403Error(error: HttpErrorResponse, request: HttpRequest<any>) {
    if (request.headers.get('notLogOutOn403')) {
      return throwError(error);
    }
    if (!request.url.endsWith('logo.svg')) {
      this.authService.onLogout();
    }
    return EMPTY;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshTokens().pipe(
        switchMap((tokens: RefreshedTokens) => {
          this.refreshTokenSubject.next(tokens);
          return next.handle(addBearerToken(request, tokens.idToken));
        }),
        finalize(() => {
          this.isRefreshing = false;
        }),
      );
    }
    return retryAfterTokenRefresh(
      this.refreshTokenSubject,
      request,
      next,
      (tokens) => tokens.idToken,
    );
  }
}
